import {ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {IVideo, VideoSubType} from '@api/models/video/video';
import {UserService} from '@core/services/user.service';
import {ColorScheme} from '@lib/models/color-scheme/color-scheme';
import {AnalyticsService} from '@utils/services/analytics/analytics.service';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {ManualScreenName} from '@utils/services/analytics/model/screen-name';
import {SubscriptionTrackingInfo} from '@lib/models/subscription/subscription-tracking-info';
import {SubscriptionContentType} from '@lib/models/subscription/subscription-content-type';
import {SubscriptionIdType} from '@lib/models/subscription/subscription-id-type';

export interface VideoHeaderContent {
  eventId?: string;
  title?: string,
  livestream?: IVideo,
  highlights?: IVideo,
  replay?: IVideo,
  youtubeLivestream?: string;
  youtubeHighlights?: string;
}

enum VideoHeaderStyle {
  Live,
  Replay,
  Highlights,
  ReplayHighlights,
  Upcoming
}

@Component({
  selector: 'app-match-video-header',
  templateUrl: './match-video-header.component.html',
  styleUrls: ['./match-video-header.component.scss']
})

export class MatchVideoHeaderComponent implements OnInit {

  @Input() content: VideoHeaderContent;
  @Input() title: string;
  @Input() colorScheme = ColorScheme.Dark;
  public style: VideoHeaderStyle;
  public video: IVideo;
  public streamStatus: string;
  public streamStatusColor: string;
  public selectedVideoType: VideoSubType;
  public youtubeUrl: SafeResourceUrl;
  public trackingInfo: SubscriptionTrackingInfo;

  readonly VideoHeaderStyle = VideoHeaderStyle;
  readonly VideoSubType = VideoSubType;

  constructor(
    public userService: UserService,
    private analyticsService: AnalyticsService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
  ) { }

  ngOnInit(): void {
    this.setVideoContent();
    this.setStreamStatus();
    this.setTrackingInfo();
  }

  private setTrackingInfo(): void {
    const contentTypeMap = {
      [VideoSubType.Highlight]: SubscriptionContentType.HIGHLIGHTS,
      [VideoSubType.Replay]: SubscriptionContentType.REPLAY
    };

    this.trackingInfo = {
      idType: SubscriptionIdType.EVENT_ID,
      id: this.content.eventId,
      // Default to livestream
      contentType: contentTypeMap[this.selectedVideoType] || SubscriptionContentType.LIVE_STREAM
    };
  }

  private setVideoContent(): void {
    if (this.content.livestream) {
      this.style = VideoHeaderStyle.Live;
      this.video = this.content.livestream;
    } else if (this.content.replay && this.content.highlights) {
      this.style = VideoHeaderStyle.ReplayHighlights;
      this.selectedVideoType = VideoSubType.Highlight;
      this.video = this.content.highlights;
    } else if (this.content.replay) {
      this.style = VideoHeaderStyle.Replay;
      this.video = this.content.replay;
    } else if (this.content.highlights) {
      this.style = VideoHeaderStyle.Highlights;
      this.video = this.content.highlights;
    } else if (this.content.youtubeLivestream) {
      this.style = VideoHeaderStyle.Live;
      this.youtubeUrl =
        this.sanitizer.bypassSecurityTrustResourceUrl(this.content.youtubeLivestream);
    } else if (this.content.youtubeHighlights) {
      this.style = VideoHeaderStyle.Highlights;
      this.youtubeUrl =
        this.sanitizer.bypassSecurityTrustResourceUrl(this.content.youtubeHighlights);
    }
  }

  private setStreamStatus(): void {
    switch (this.style) {
      case VideoHeaderStyle.Live:
        this.streamStatus = 'LIVESTREAM';
        this.streamStatusColor = '#FF0000';
        break;
      case VideoHeaderStyle.Highlights:
        this.streamStatus = 'HIGHLIGHTS';
        this.streamStatusColor = '#9747FF';
        break
      case VideoHeaderStyle.Replay:
        this.streamStatus = 'REPLAY';
        this.streamStatusColor = '#517BBB';
    }
  }

  public onSwitchClick(type: VideoSubType): void {
    if (type === this.selectedVideoType) {
      return;
    }
    this.analyticsService.sendPageView({ manualScreenName: ManualScreenName.VIDEO_SWITCH})
    this.selectedVideoType = type;
    this.video = undefined;
    this.cdr.detectChanges()
    switch (type) {
      case VideoSubType.Highlight:
        this.video = this.content.highlights;
        break
      case VideoSubType.Replay:
        this.video = this.content.replay;
    }
    this.setTrackingInfo();
  }
}
